import React, { useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useSpring } from "framer-motion";
import { Play, Plus } from "lucide-react";
import {
  MediaControlBar,
  MediaController,
  MediaMuteButton,
  MediaPlayButton,
  MediaTimeRange,
} from "media-chrome/react";

export default function VideoPlayer({ src, className, isIphone }) {
  const [showVideoPopOver, setShowVideoPopOver] = useState(false);

  const spring = { mass: 0.1 };
  const x = useSpring(0, spring);
  const y = useSpring(0, spring);
  const opacity = useSpring(0, spring);

  const handlePointerMove = (e) => {
    opacity.set(1);
    const bounds = e.currentTarget.getBoundingClientRect();
    x.set(e.clientX - bounds.left);
    y.set(e.clientY - bounds.top);
  };

  return (
    <section
      className={`relative flex h-full w-full items-center justify-center bg-[#f5f4f3] overflow-hidden ${className || ""}`}
    >
      <AnimatePresence>
        {showVideoPopOver && (
          <VideoPopOver src={src} setShowVideoPopOver={setShowVideoPopOver} isIphone={isIphone} />
        )}
      </AnimatePresence>

      <div
        onMouseMove={handlePointerMove}
        onMouseLeave={() => opacity.set(0)}
        onClick={() => setShowVideoPopOver(true)}
        className="w-full h-full cursor-pointer relative"
      >
        <motion.div
          style={{ x, y, opacity }}
          className="absolute pointer-events-none z-20 flex w-fit select-none items-center justify-center gap-2 p-2 text-sm text-white mix-blend-exclusion"
        >
          <Play className="h-4 w-4 fill-white" /> Play
        </motion.div>
        <video
          autoPlay
          muted
          playsInline
          loop
          className="pointer-events-none h-full w-full object-cover"
        >
          <source src={src} />
        </video>
      </div>
    </section>
  );
}

const VideoPopOver = ({ src, setShowVideoPopOver, isIphone }) => {
  return createPortal(
    <div className="fixed left-0 top-0 z-9999 flex h-screen w-screen items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-black/90 absolute left-0 top-0 h-full w-full backdrop-blur-lg"
        onClick={() => setShowVideoPopOver(false)}
      />

      <motion.div
        initial={{ clipPath: "circle(0% at 50% 50%)" }}
        animate={{ clipPath: "circle(150% at 50% 50%)" }}
        exit={{
          clipPath: "circle(0% at 50% 50%)",
          transition: {
            duration: 0.8,
            type: "spring",
            stiffness: 100,
            damping: 20,
          },
        }}
        transition={{
          duration: 0.8,
          type: "spring",
          stiffness: 50,
          damping: 20,
        }}
        className="relative h-full w-full bg-black"
      >
        <MediaController className="h-full w-full">
          <video
            src={src}
            autoPlay
            slot="media"
            className={`h-full w-full object-contain ${isIphone ? "py-16 md:py-24" : ""}`}
          />

          <span
            onClick={() => setShowVideoPopOver(false)}
            className="absolute right-6 top-6 z-10 cursor-pointer rounded-full p-2 transition-colors hover:bg-white/20"
          >
            <Plus className="h-8 w-8 rotate-45 text-white" />
          </span>

          <MediaControlBar className="absolute bottom-0 left-1/2 flex w-full -translate-x-1/2 items-center justify-center px-5 mix-blend-exclusion md:px-10 md:py-8">
            <MediaPlayButton className="h-4 bg-transparent text-white" />
            <MediaTimeRange className="[--media-range-track-height:2px] bg-transparent text-white" />
            <MediaMuteButton className="h-4 w-4 bg-transparent text-white" />
          </MediaControlBar>
        </MediaController>
      </motion.div>
    </div>,
    document.body
  );
};